import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'
import {
  Children,
  cloneElement,
  createContext,
  Fragment,
  isValidElement,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react'
import { Link } from '../lib/Link'

/** Set by PreferenceCategory when it wraps children in a <form>. */
const PreferenceFormContext = createContext(false)

function flattenChildren(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = []
  Children.forEach(children, (child) => {
    if (child == null || child === false || child === true) return
    if (!isValidElement(child)) return
    if (child.type === Fragment) {
      out.push(
        ...flattenChildren((child.props as { children?: ReactNode }).children)
      )
      return
    }
    out.push(child)
  })
  return out
}

type Fillable = {
  filled?: boolean
}

type PreferenceCategoryProps = Fillable & {
  children: React.ReactNode
  title?: string
}

type PreferenceLinkProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  href?: string
  isDestructive?: boolean
  target?: string
}

type PreferenceSwitchProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  value: boolean
  onValueChange: (value: boolean) => void
  /** `light` for white/light surfaces (e.g. order modal). Default: dark settings UI. */
  variant?: 'dark' | 'light'
}

type PreferenceButtonProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  onPress: () => void
  isDestructive?: boolean
  disabled?: boolean
  variant?: 'dark' | 'light'
}

type PreferenceSubmitProps = {
  title: string
  onPress: () => void
  disabled?: boolean
  /** Accepted so PreferenceCategory can pass filled; never applied. */
  filled?: boolean
}

type PreferenceInfoProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  className?: string
}

interface PreferenceDivProps
  extends Fillable,
    React.HTMLAttributes<HTMLDivElement> {}

type PreferenceSelectProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  value?: string
  options: Array<{ value: string; label: string; summary?: string }>
  onValueChange: (value: string) => void
}

type PreferencePromptProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  value: string
  onChange: (value: string) => void
}

type PreferenceInputProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  value: string
  onChange: (value: string) => void
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>

type PreferenceUploadProps = Fillable & {
  title: string
  summary?: string
  icon?: React.ReactNode
  accept?: string
  onFileSelect: (file: File) => void
  isUploading?: boolean
}

const rowBase = 'w-full flex items-center pl-5 pr-4 py-4'
const rowHover = 'hover:bg-white/10'
const rowFilled = 'bg-green-900 first:rounded-t-2xl last:rounded-b-2xl rounded'

export function BackButton({
  children,
  href,
}: {
  children?: React.ReactNode
  href: string
}) {
  return (
    <Link href={href} className="flex p-1 group shrink-0">
      <div className="flex group-hover:bg-white/10 rounded-full p-3 transition-transform">
        <ArrowLeftIcon className="w-6 h-6 text-white opacity-60" />
      </div>
      {children}
    </Link>
  )
}

export function ForwardButton({
  children,
  href,
  title: ariaTitle,
}: {
  children?: React.ReactNode
  href: string
  title?: string
}) {
  return (
    <Link
      href={href}
      className="flex p-1 group shrink-0"
      title={ariaTitle}
      aria-label={ariaTitle}
    >
      {children}
      <div className="flex group-hover:bg-white/10 rounded-full p-3 transition-transform">
        <ArrowRightIcon className="w-6 h-6 text-white opacity-60" />
      </div>
    </Link>
  )
}

const titleRowClass = 'flex items-center gap-0 mb-2 min-h-14'

export function PreferenceHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className={titleRowClass}>
      <h1 className="text-3xl font-semibold opacity-40 px-1">{children}</h1>
    </div>
  )
}

/** Sub-screen title with optional Android-style back / next affordances. */
export function PreferenceScreen({
  title,
  backHref,
  nextHref,
  nextTitle,
  children,
}: {
  title: string
  backHref?: string
  nextHref?: string
  nextTitle?: string
  children: React.ReactNode
}) {
  const edgePad = [backHref ? '-ml-2' : '', nextHref ? '-mr-2' : '']
    .filter(Boolean)
    .join(' ')
  return (
    <>
      <div className={`${titleRowClass}${edgePad ? ` ${edgePad}` : ''}`}>
        {backHref ? <BackButton href={backHref} /> : null}
        <h1 className="text-3xl font-medium opacity-60 px-1 min-w-0 flex-1 truncate">
          {title}
        </h1>
        {nextHref ? <ForwardButton href={nextHref} title={nextTitle} /> : null}
      </div>
      {children}
    </>
  )
}

export function PreferenceCategory({
  children,
  title,
  filled = false,
}: PreferenceCategoryProps) {
  const flat = flattenChildren(children)
  const submits = flat.filter((c) => c.type === PreferenceSubmit)
  const rows = flat.filter((c) => c.type !== PreferenceSubmit)
  const submitProps = submits[0]?.props as PreferenceSubmitProps | undefined

  let items: ReactNode
  if (filled) {
    const filledRows = rows.map((c, i) =>
      cloneElement(c, { filled: true, key: c.key ?? i } as object)
    )
    const after = submits.map((c, i) =>
      cloneElement(c, { key: c.key ?? `submit-${i}` })
    )
    items = (
      <>
        {filledRows.length > 0 ? (
          <div className="flex flex-col gap-px overflow-hidden rounded-2xl text-white">
            {filledRows}
          </div>
        ) : null}
        {after}
      </>
    )
  } else {
    items = children
  }

  if (submitProps) {
    const { onPress, disabled } = submitProps
    items = (
      <PreferenceFormContext.Provider value={true}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (disabled) return
            onPress()
          }}
        >
          {items}
        </form>
      </PreferenceFormContext.Provider>
    )
  }

  return (
    <div className="my-4">
      {title && (
        <h2 className="text-sm font-medium pb-2 ml-3 opacity-70">{title}</h2>
      )}
      {items}
    </div>
  )
}

export function PreferenceLink({
  title,
  summary,
  icon,
  href,
  filled,
  target,
}: PreferenceLinkProps) {
  return (
    <Link
      href={href || ''}
      className={`${rowBase} ${rowHover} ${filled ? rowFilled : ''}`}
      target={target}
    >
      {iconContainer(icon)}
      {titleContainer(title, summary)}
    </Link>
  )
}

export function PreferenceSwitch({
  title,
  summary,
  icon,
  value,
  onValueChange,
  filled,
  variant = 'dark',
}: PreferenceSwitchProps) {
  const light = variant === 'light'
  return (
    <button
      type="button"
      onClick={() => onValueChange(!value)}
      className={`${rowBase} pr-6 ${
        light ? 'hover:bg-slate-100' : rowHover
      } ${filled ? rowFilled : ''}`}
    >
      {iconContainer(icon)}
      {titleContainer(title, summary)}
      <div
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          value
            ? 'bg-green-600'
            : light
              ? 'bg-slate-300 ring-1 ring-slate-400/60'
              : 'bg-white/20'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
            light && !value ? 'bg-white shadow-sm' : 'bg-white'
          } ${value ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </div>
    </button>
  )
}

export function PreferenceButton({
  title,
  summary,
  icon,
  onPress,
  isDestructive = false,
  disabled = false,
  filled,
  variant = 'dark',
}: PreferenceButtonProps) {
  const light = variant === 'light'
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      className={`${rowBase} ${
        isDestructive
          ? 'text-red-300/90 hover:bg-red-600/30'
          : light
            ? 'hover:bg-slate-100'
            : rowHover
      } ${filled ? rowFilled : ''} disabled:opacity-50`}
    >
      {iconContainer(icon)}
      {titleContainer(title, summary)}
    </button>
  )
}

/** Material filled button, right-aligned — for save / submit actions.
 *  Must be a child of PreferenceCategory (wrapped in a form so Enter submits). */
export function PreferenceSubmit({
  title,
  disabled = false,
}: PreferenceSubmitProps) {
  const inForm = useContext(PreferenceFormContext)
  if (!inForm) {
    throw new Error(
      'PreferenceSubmit must be a child of PreferenceCategory so it is wrapped in a <form>.'
    )
  }

  return (
    <div className="w-full flex justify-end py-3">
      <button
        type="submit"
        disabled={disabled}
        className="rounded-full bg-green-600 hover:bg-green-500 active:bg-green-700 text-white px-6 py-2.5 text-sm font-medium tracking-wide disabled:opacity-40 disabled:pointer-events-none transition-colors"
      >
        {title}
      </button>
    </div>
  )
}

export function PreferenceInfo({
  title,
  summary,
  icon,
  filled,
  className,
}: PreferenceInfoProps) {
  return (
    <div className={`${rowBase} ${filled ? rowFilled : ''} ${className ?? ''}`}>
      {iconContainer(icon)}
      {titleContainer(title, summary)}
    </div>
  )
}

export function PreferenceDiv({
  filled,
  className,
  ...props
}: PreferenceDivProps) {
  return (
    <div
      {...props}
      className={`w-full ${filled ? rowFilled : ''} ${className ?? ''}`}
    />
  )
}

export function PreferenceSelect({
  title,
  summary,
  icon,
  value,
  options,
  onValueChange,
  filled,
}: PreferenceSelectProps) {
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onValueChange(event.target.value)
  }

  const selected = options.find((option) => option.value === value)
  return (
    <label
      className={`w-full relative flex items-center justify-between pr-4 py-4 ${
        filled ? rowFilled : ''
      }`}
    >
      <div className="flex items-center pl-5">
        {iconContainer(icon)}
        {titleContainer(title, selected?.summary || summary)}
      </div>
      <select
        value={value}
        onChange={handleChange}
        className="absolute opacity-0 inset-0 appearance-none"
        aria-label={`Select ${title.toLowerCase()}`}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="bg-green-950 text-white"
          >
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

export function PreferencePrompt({
  title,
  summary,
  icon,
  value,
  onChange,
  filled,
}: PreferencePromptProps) {
  const handlePress = () => {
    const newValue = window.prompt(title, value)
    if (newValue !== null) {
      onChange(newValue)
    }
  }

  return (
    <button
      type="button"
      onClick={handlePress}
      className={`w-full flex items-center justify-between pr-4 py-4 ${rowHover} ${
        filled ? rowFilled : ''
      }`}
    >
      <div className="flex items-center pl-5">
        {iconContainer(icon)}
        {titleContainer(title, value || ' ')}
      </div>
    </button>
  )
}

export function PreferenceInput({
  title,
  summary,
  icon,
  value,
  onChange,
  filled,
  ...rest
}: PreferenceInputProps) {
  return (
    <label
      className={`w-full flex items-center justify-between pr-4 py-4 hover:bg-white/10 focus-within:!bg-white/15 ${
        filled ? rowFilled : ''
      }`}
    >
      <div className="flex items-center pl-5 flex-1 min-w-0">
        {iconContainer(icon)}
        <div className="flex-1 min-w-0">
          <div className="pl-1 text-xl leading-snug">{title}</div>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="pl-1 w-full bg-transparent border-none text-base opacity-70 leading-snug focus:outline-none focus:opacity-100 placeholder:text-white/40"
            {...rest}
          />
        </div>
      </div>
    </label>
  )
}

export function PreferenceUpload({
  title,
  summary,
  icon,
  accept,
  onFileSelect,
  isUploading = false,
  filled,
}: PreferenceUploadProps) {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
    event.target.value = ''
  }

  return (
    <label
      className={`relative w-full flex items-center justify-between pr-4 py-4 ${rowHover} cursor-pointer ${
        filled ? rowFilled : ''
      } ${isUploading ? 'opacity-50 cursor-not-allowed text-green-200' : 'text-white'}`}
    >
      <div className="flex items-center pl-5">
        {iconContainer(icon)}
        {titleContainer(title, summary)}
      </div>
      <input
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={isUploading}
        className="opacity-0 absolute inset-0"
      />
      <div className="flex items-center space-x-2">
        {isUploading && (
          <div className="w-4 h-4 border-2 border-green-300 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    </label>
  )
}

function iconContainer(icon?: React.ReactNode) {
  return icon ? (
    <div className="text-current/70 w-9 h-6 pr-3 shrink-0">{icon}</div>
  ) : null
}

function titleContainer(title: string, summary?: string) {
  return (
    <div className="pl-1 text-left flex-1 min-w-0">
      <div className="text-xl leading-snug">{title}</div>
      {summary && (
        <div className="text-base opacity-70 leading-snug whitespace-pre-line">
          {summary === ' ' ? <>&nbsp;</> : summary}
        </div>
      )}
    </div>
  )
}
